import { useState } from 'react'
import { router } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ArrowLeft, Waves } from 'lucide-react'
import type { BoardType, EquipmentKind } from '@/types/equipment'
import { boardTypeLabel, BOARD_SIZES } from '@/lib/equipment'

interface Props {
  board_types: BoardType[]
  kinds: EquipmentKind[]
}

export default function EquipmentsNew({ board_types, kinds }: Props) {
  const [form, setForm] = useState({
    kind: kinds[0] || 'surfboard',
    board_type: '' as BoardType | '',
    size: '',
    quantity: '1',
    nickname: '',
    description: ''
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    router.post('/equipments', { equipment: form })
  }

  const canSubmit = form.kind && form.board_type && form.size.trim() && Number(form.quantity) >= 1

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => router.visit('/equipments')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
              <Waves className="h-5 w-5 text-primary" />
            </div>
            <h1 className="text-2xl font-bold">Novo Equipamento</h1>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Dados da prancha</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo de prancha *</Label>
                  <Select
                    value={form.board_type}
                    onValueChange={(v) => setForm({ ...form, board_type: v as BoardType })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {board_types.map((bt) => (
                        <SelectItem key={bt} value={bt}>{boardTypeLabel(bt)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tamanho *</Label>
                  <Select
                    value={form.size}
                    onValueChange={(v) => setForm({ ...form, size: v })}
                  >
                    <SelectTrigger className="font-mono">
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {BOARD_SIZES.map((s) => (
                        <SelectItem key={s} value={s} className="font-mono">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="quantity">Quantidade *</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="1"
                  step="1"
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="nickname">Apelido / Identificação</Label>
                <Input
                  id="nickname"
                  value={form.nickname}
                  onChange={(e) => setForm({ ...form, nickname: e.target.value })}
                  placeholder="Ex: Conjunto vermelho"
                />
              </div>

              <div>
                <Label htmlFor="description">Observações</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Detalhes opcionais sobre esse equipamento"
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" className="flex-1" onClick={() => router.visit('/equipments')}>
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1" disabled={!canSubmit}>
                  Cadastrar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
